import React, { useEffect, useMemo, useRef, useState } from "react";
import apiRoutes from "../../../api/apiRoutes";
import { apiFetchJson , appFetchJson} from "../../../utils/http";

/**
 * Modal con pestañas: Histórico + Inactivar Descuentos
 */
export default function InactivarDescuentos({ cliente, onClose, onSaved }) {
  const [activeTab, setActiveTab] = useState("historico");

  return (
    <Modal title="Descuentos" onClose={onClose}>
      {/* === ENCABEZADO CLIENTE === */}
      <div className="section-panel mb-3">
        <div className="form-row gap-2">
          <div className="form-label font-medium">Cliente:</div>
          <div className="flex-1 py-2 text-gray-800">
            {cliente ? (
              <>
                <b>{cliente.codigo ?? cliente.id}</b>
                {cliente.nombre ? ` — ${cliente.nombre}` : ""}
              </>
            ) : (
              <span className="text-gray-500">Sin cliente</span>
            )}
          </div>
        </div>
      </div>

      {/* === TABS === */}
      <div className="border-b mb-4 flex gap-6 px-2">
        <button
          className={`pb-2 ${
            activeTab === "historico"
              ? "border-b-2 border-green-600 text-green-700 font-semibold"
              : "text-gray-600"
          }`}
          onClick={() => setActiveTab("historico")}
        >
          Histórico
        </button>

        <button
          className={`pb-2 ${
            activeTab === "eliminar"
              ? "border-b-2 border-green-600 text-green-700 font-semibold"
              : "text-gray-600"
          }`}
          onClick={() => setActiveTab("eliminar")}
        >
          Inactivar Descuentos
        </button>
      </div>

      {activeTab === "historico" ? (
        <Historico cliente={cliente} onClose={onClose} />
      ) : (
        <InactivarDescuentosTab cliente={cliente} onClose={onClose} onSaved={onSaved} />
      )}
    </Modal>
  );
}

/* -----------------------------------------------------------
   📌 OBTENCIÓN ÚNICA DE LOS DESCUENTOS (activos+inactivos)
----------------------------------------------------------- */
async function fetchDescuentos(clienteId, { estado, page = 0, size = 20 } = {}) {
  const CLIENTES_BASE = (apiRoutes?.clientes?.list || "").replace(/\/list(?:\?.*)?$/, "");

  const params = new URLSearchParams();
  params.set("page", String(page));
  params.set("size", String(size));
  if (estado) params.set("estado", estado);

  const url = `${CLIENTES_BASE}/${encodeURIComponent(clienteId)}/descuentos?${params.toString()}`;

  return apiFetchJson(url);
}

async function fetchAllDescuentos(clienteId, { estado, size = 100 } = {}) {
  let page = 0;
  let hasMore = true;
  const acc = [];

  while (hasMore) {
    const data = await fetchDescuentos(clienteId, { estado, page, size });
    acc.push(...(data?.items ?? []));
    hasMore = Boolean(data?.hasMore);
    page = (data?.page ?? page) + 1;
  }

  return acc;
}

/* -----------------------------------------------------------
   TAB 1 — HISTÓRICO: usa los descuentos INACTIVOS
----------------------------------------------------------- */
function Historico({ cliente, onClose }) {
  const [loading, setLoading] = useState(true);
  const [inactivos, setInactivos] = useState([]);

  useEffect(() => {
    let alive = true;

    if (!cliente?.id) return;

    (async () => {
      try {
        setLoading(true);
        setInactivos([]);

        const items = await fetchAllDescuentos(cliente.id, {
          estado: "INACTIVO",
          size: 100,
        });

        if (!alive) return;

        setInactivos(items);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [cliente?.id]);

  if (loading) return <div className="text-center py-10">Cargando…</div>;

  return (
    <div className="section-panel">
      <div className="text-gray-700 mb-3">
        Descuentos inactivos del cliente: <b>{inactivos.length}</b>
      </div>

      <div
        className="space-y-2 overflow-auto pr-1"
        style={{ maxHeight: "360px", minHeight: "120px" }}
      >
        {inactivos.length === 0 && (
          <div className="text-gray-500 text-sm">No hay descuentos inactivos.</div>
        )}

        {inactivos.map((d) => (
          <div
            key={d.id}
            className="px-3 py-2 rounded border bg-gray-50 shadow-sm text-sm"
          >
            {`${d.vlDescuento ? `${d.vlDescuento} Gs | ` : ""}${
              d.prDescuento ? `${d.prDescuento}% | ` : ""
            }PROD: ${d.productoNombre ?? "TODOS"} | EESS: ${
              d.eessNombre ?? "TODAS"
            } | FIN: ${d.dataFin ?? "-"}`}
          </div>
        ))}
      </div>

      <div className="mt-4 flex justify-end">
        <button type="button" className="btn-fil" onClick={onClose}>
          Cancelar
        </button>
      </div>
    </div>
  );
}

/* -----------------------------------------------------------
   TAB 2 — INACTIVAR DESCUENTOS: usa los ACTIVOS
----------------------------------------------------------- */
/* -----------------------------------------------------------
   TAB 2 — INACTIVAR DESCUENTOS (con mejoras UX)
----------------------------------------------------------- */
function InactivarDescuentosTab({ cliente, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [descuentos, setDescuentos] = useState([]); 
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [search, setSearch] = useState("");
  const [openPicker, setOpenPicker] = useState(false);

  const pickerRef = useRef(null);
  const buttonRef = useRef(null);

  const toId = (v) => (v != null ? String(v) : "");

  const CLIENTES_BASE = (apiRoutes?.clientes?.list || "").replace(
    /\/list(?:\?.*)?$/,
    ""
  );
  const DESCUENTOS_CERRAR_URL = (clienteId) =>
    `${CLIENTES_BASE}/${encodeURIComponent(clienteId)}/descuentos/cerrar`;

  /* =========================================================
     👉 Cerrar el picker al hacer clic fuera
  ========================================================= */
  useEffect(() => {
    function handleClickOutside(e) {
      if (
        openPicker &&
        pickerRef.current &&
        !pickerRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target)
      ) {
        setOpenPicker(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openPicker]);


  /* =========================================================
     Cargar datos
  ========================================================= */
  useEffect(() => {
    let alive = true;

    if (!cliente?.id) return;

    (async () => {
      try {
        setLoading(true);
        const items = await fetchAllDescuentos(cliente.id, {
          estado: "ACTIVO",
          size: 100,
        });

        if (!alive) return;

        setDescuentos(items);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => (alive = false);
  }, [cliente?.id]);


  const filtrados = useMemo(() => {
    if (!search) return descuentos;
    const q = search.toLowerCase();
    return descuentos.filter((d) =>
      `${d.id} ${d.productoNombre} ${d.eessNombre}`.toLowerCase().includes(q)
    );
  }, [descuentos, search]);


  const toggleOne = (id) => {
    const key = toId(id);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const marcarTodas = () =>
    setSelectedIds(new Set(filtrados.map((d) => toId(d.id))));

  const desmarcarTodas = () => setSelectedIds(new Set());


  /* =========================================================
     Guardar
  ========================================================= */
  const handleSave = async (e) => {
    e.preventDefault();
    if (saving || selectedIds.size === 0) return;

    setSaving(true);

    try {
      const url = DESCUENTOS_CERRAR_URL(cliente.id);
      const ids = Array.from(selectedIds).map(Number);

      await appFetchJson(url, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ descuentoIds: ids }),
      });

      onSaved?.({ seleccionadas: ids.length });
      onClose?.();
    } finally {
      setSaving(false);
    }
  };


  /* =========================================================
     RENDER
  ========================================================= */
  return (
    <form onSubmit={handleSave} className="space-y-4">
      {loading ? (
        <div className="text-center py-10">Cargando…</div>
      ) : (
        <>
          <div className="text-gray-700 mb-2">
            Descuentos activos del cliente: <b>{descuentos.length}</b>
          </div>

          {/* SELECTOR */}
<div className="relative">
  <button
    ref={buttonRef}
    type="button"
    className="w-full border rounded px-3 py-2 bg-white text-left"
    onClick={() => setOpenPicker((o) => !o)}
  >
    {selectedIds.size === 0
      ? "Seleccionar"
      : `${selectedIds.size} seleccionados`}
  </button>

  {openPicker && (
    <div
      ref={pickerRef}
      className="mt-2 w-full rounded border bg-white shadow-lg"
    >
      {/* FILTRO + OPCIONES */}
      <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50 gap-3">
        <input
          className="border px-2 py-1 flex-1 rounded"
          placeholder="Filtrar registro"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <button
          type="button"
          onClick={marcarTodas}
          className="text-green-700 text-sm hover:underline"
        >
          ✓ Marcar todas
        </button>

        <button
          type="button"
          onClick={desmarcarTodas}
          className="text-green-700 text-sm hover:underline"
        >
          ✗ Desmarcar todas
        </button>
      </div>

      {/* LISTA CON SCROLL GARANTIZADO */}
      <div
        className="overflow-auto"
        style={{ maxHeight: "260px", minHeight: "100px" }}
      >
        {filtrados.map((d) => (
          <label
            key={d.id}
            className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
          >
            <input
              type="checkbox"
              checked={selectedIds.has(toId(d.id))}
              onChange={() => toggleOne(d.id)}
            />
            <span>
              {d.vlDescuento ? `Valor: ${d.vlDescuento} | ` : ""}
              {d.prDescuento ? `%: ${d.prDescuento} | ` : ""}
              PROD: {d.productoNombre} | EESS: {d.eessNombre}
            </span>
          </label>
        ))}

        {filtrados.length === 0 && (
          <div className="px-3 py-3 text-gray-500 text-sm">
            Sin resultados
          </div>
        )}
      </div>

      <div className="p-2 border-t text-right bg-white">
        <button
          type="button"
          className="text-green-700"
          onClick={() => setOpenPicker(false)}
        >
          Cerrar
        </button>
      </div>
    </div>
  )}
</div>


          {/* BOTONES GENERALES */}
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="submit"
              disabled={selectedIds.size === 0 || saving}
              className="btn-fil disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {saving ? "Guardando…" : "Guardar"}
            </button>

            <button type="button" className="btn-fil" onClick={onClose}>
              Cancelar
            </button>
          </div>
        </>
      )}
    </form>
  );
}


/* -----------------------------------------------------------
   MODAL
----------------------------------------------------------- */
function Modal({ title, children }) {
  return (
    <div className="modal-backdrop">
      <div className="modal-panel-md">
        <div className="modal-header flex justify-between items-center">
          <h2 className="section-title">{title}</h2>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}
